/**
 * Acesso de pesquisa: os cenários do Google WeatherNext 2.
 *
 * Os termos do Google para a previsão em tempo real do WeatherNext 2
 * (https://storage.googleapis.com/weathernext-public/terms-of-use.pdf) só
 * permitem, além do uso interno, a distribuição controlada a pessoas
 * identificadas, para fins educacionais. Por isso o dado fica atrás de senha
 * no servidor e nada dele aparece para o público: a entrada é o link com
 * `?pesquisa`, que abre o pedido de usuário e senha (`AcessoPesquisa.tsx`).
 *
 * A credencial (`Basic …`, a senha em base64) fica só nesta aba
 * (`sessionStorage`); com "Lembrar neste navegador", no `localStorage`. Sem
 * ela, basta entrar de novo. Nunca vai para o link nem para o código publicado.
 */
import { HttpError } from '../lib/fetchRetry';
import { carregarIndiceWn2, definirCredencialWn2, type IndicePrevisao } from './api';
import { usePrevisaoStore } from './store';

const CHAVE = 'amaview-pesquisa-wn2';

function guardada(): string | null {
  for (const armazem of ['sessionStorage', 'localStorage'] as const) {
    try {
      const c = globalThis[armazem].getItem(CHAVE);
      if (c) return c;
    } catch {
      // Sem armazenamento (aba anônima, bloqueio): segue sem.
    }
  }
  return null;
}

function guardar(c: string | null, lembrar = false): void {
  for (const armazem of ['sessionStorage', 'localStorage'] as const) {
    try {
      if (c && (armazem === 'localStorage') === lembrar) globalThis[armazem].setItem(CHAVE, c);
      else globalThis[armazem].removeItem(CHAVE);
    } catch {
      // Sem armazenamento: o acesso vale até recarregar a página.
    }
  }
}

/** `Basic` com o usuário e a senha em UTF-8. */
export function credencialDe(usuario: string, senha: string): string {
  const bytes = new TextEncoder().encode(`${usuario}:${senha}`);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return `Basic ${btoa(bin)}`;
}

let credencial: string | null = guardada();
definirCredencialWn2(credencial);

/** Há uma credencial guardada (o índice confirma se ela ainda vale). */
export function temAcessoGuardado(): boolean {
  return credencial !== null;
}

type IndiceWn2 = Awaited<ReturnType<typeof carregarIndiceWn2>>;

/** O índice com o WeatherNext 2 juntado (ou sem ele). */
function comWn2(indice: IndicePrevisao, wn2: IndiceWn2 | null): IndicePrevisao {
  const modelos = { ...indice.modelos };
  delete modelos.wn2;
  if (wn2) modelos.wn2 = wn2.modelo;
  return { ...indice, modelos, termosWn2: wn2?.termos };
}

/** O WeatherNext 2 que está na tela agora (para não sumir numa falha passageira). */
function wn2NaTela(): IndiceWn2 | null {
  const i = usePrevisaoStore.getState().indice;
  return i?.modelos.wn2 ? { modelo: i.modelos.wn2, termos: i.termosWn2 } : null;
}

/**
 * Junta o WeatherNext 2 ao índice público, se houver acesso.
 * - Senha recusada (trocada no servidor): apaga a guardada.
 * - Outra falha (rede, servidor): fica o WeatherNext 2 que já estava na tela;
 *   a próxima leitura tenta de novo.
 * - Se a pessoa entrou ou saiu enquanto o pedido ia e vinha, vale o estado de
 *   AGORA: a resposta da credencial velha não traz os cenários de volta depois
 *   de "Sair", e um 401 dela não apaga a credencial nova.
 */
export async function juntarWn2(indice: IndicePrevisao, signal?: AbortSignal): Promise<IndicePrevisao> {
  const c = credencial;
  if (!c) return comWn2(indice, null);
  let wn2: IndiceWn2 | null;
  try {
    wn2 = await carregarIndiceWn2(c, signal);
  } catch (e) {
    if (credencial !== c) return comWn2(indice, credencial ? wn2NaTela() : null);
    if (e instanceof HttpError && e.status === 401) {
      esquecer();
      return comWn2(indice, null);
    }
    return comWn2(indice, wn2NaTela());
  }
  if (credencial !== c) return comWn2(indice, credencial ? wn2NaTela() : null);
  usePrevisaoStore.setState({ pesquisa: true });
  return comWn2(indice, wn2);
}

function esquecer(): void {
  credencial = null;
  definirCredencialWn2(null);
  guardar(null);
  usePrevisaoStore.setState({ pesquisa: false });
}

/** Tenta entrar: confere a senha no servidor; dá certo → guarda e põe os cenários no índice. */
export async function entrarNaPesquisa(usuario: string, senha: string, lembrar = false): Promise<'ok' | 'recusado' | 'falhou'> {
  const c = credencialDe(usuario.trim(), senha);
  try {
    const wn2 = await carregarIndiceWn2(c);
    credencial = c;
    definirCredencialWn2(c);
    guardar(c, lembrar);
    const s = usePrevisaoStore.getState();
    usePrevisaoStore.setState({
      pesquisa: true,
      pedirAcesso: false,
      ...(s.indice ? { indice: comWn2(s.indice, wn2) } : {}),
      // A rodada do WeatherNext 2 é fixada como as outras ao entrar (já no modo: agora).
      ...(s.modo && wn2.modelo.rodadas[0] ? { rodadas: { ...s.rodadas, wn2: wn2.modelo.rodadas[0] } } : {}),
    });
    return 'ok';
  } catch (e) {
    return e instanceof HttpError && (e.status === 401 || e.status === 403) ? 'recusado' : 'falhou';
  }
}

/** Sai do acesso de pesquisa: some a credencial, os cenários do índice e, se estavam no mapa, a camada volta à chuva. */
export function sairDaPesquisa(): void {
  esquecer();
  const s = usePrevisaoStore.getState();
  const rodadas = { ...s.rodadas };
  delete rodadas.wn2;
  usePrevisaoStore.setState({
    rodadas,
    ...(s.indice ? { indice: comWn2(s.indice, null) } : {}),
    ...(s.variavel === 'cenarios' ? { variavel: 'chuva' as const } : {}),
  });
}
